import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDownIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface SearchableMultiSelectProps {
  label: string;
  description: string;
  options: string[];
  selectedValues: string[];
  onOptionChange: (option: string, checked: boolean) => void;
  onClearAll: () => void;
  onSelectAll: () => void;
  placeholder?: string;
  isLoading?: boolean;
  color?: string;
}

/**
 * Searchable multi-select component with string filtering
 * Ideal for large lists like failure modes where users need to search
 */
const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  description,
  options,
  selectedValues,
  onOptionChange,
  onClearAll,
  onSelectAll,
  placeholder = 'Select options...',
  isLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchLower = searchTerm.toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(searchLower));
  }, [options, searchTerm]);

  // Calculate dropdown position based on available space
  useEffect(() => {
    if (isOpen && containerRef.current && dropdownRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 240px
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      // Use bottom position if there's enough space, otherwise use top
      setDropdownPosition(spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top');
    }
  }, [isOpen]);

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <label className="block text-sm onyx-subtitle text-gray-700 mb-2">
        {label}
        <span className="text-gray-400 onyx-body ml-1">({options.length})</span>
      </label>

      <div className="relative" ref={containerRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left hover:border-gray-400 focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue disabled:opacity-50 transition-colors onyx-body"
        >
          <span className="text-sm text-gray-700">
            {selectedValues.length === 0 ? placeholder : `${selectedValues.length} selected`}
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className={`absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
          >
            <div className="p-2">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
                <span className="text-xs onyx-subtitle text-gray-500 uppercase">{description}</span>
                {selectedValues.length < options.length && (
                  <button
                    onClick={onSelectAll}
                    className="text-xs text-blue-600 hover:text-blue-800 onyx-subtitle transition-colors"
                  >
                    Select All
                  </button>
                )}
                {selectedValues.length > 0 && (
                  <button
                    onClick={onClearAll}
                    className="text-xs text-onyx-red hover:text-red-800 onyx-subtitle transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Search input */}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search failure modes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-8 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {filteredOptions.length === 0 ? (
                <div className="p-2 text-center text-gray-500 onyx-body text-sm">
                  {searchTerm ? 'No matching failure modes found' : 'No options available'}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => onOptionChange(option, e.target.checked)}
                      className="rounded border-gray-300 text-onyx-bright-blue focus:ring-onyx-bright-blue"
                    />
                    <span className="text-sm text-gray-700 flex-1 onyx-body">{option}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableMultiSelect;
